import type { OnboardingStep, ConnectionType } from '../hooks/useOnboardingState'

// Формат startapp параметра: step_type_entityId или step_type_entityId_counterId или step_type_entityId_counterId_yandexLogin
// Примеры:
// - success_channel_-1001234567890 → step: success, type: channel, entityId: -1001234567890
// - success_paid_channel_-1001234567890 → step: success, type: channel, entityId: -1001234567890, isPaid: true
// - success-direct_channel_-1001234567890_12345678 → step: success-direct, type: channel, entityId: -1001234567890, counterId: 12345678
// - success-direct_channel_-1001234567890_12345678_user123 → step: success-direct, type: channel, entityId: -1001234567890, counterId: 12345678, yandexLogin: user123
// - success-direct_channel_-1001234567890_0_user123 → step: success-direct (ошибка), yandexLogin: user123
// - connection_channel → step: connection, type: channel
// - welcome → step: welcome
export interface ParsedStartParam {
  step: OnboardingStep
  type: ConnectionType
  entityId: string
  isPaid?: boolean // Флаг успешной оплаты Trial
  counterId?: number // ID счётчика Яндекс Метрики (для success-direct)
  yandexLogin?: string // Логин Яндекс (для success-direct)
}

// Маппинг коротких названий на полные
const stepMap: Record<string, OnboardingStep> = {
  success: 'success',
  'success-direct': 'success-direct',
  connection: 'connection',
  welcome: 'welcome',
  start: 'start',
  loading: 'loading',
  'channel-connection': 'channel-connection',
  'bot-connection': 'bot-connection',
  'ads-connection': 'ads-connection',
  payment: 'payment',
}

const typeMap: Record<string, ConnectionType> = {
  channel: 'channel',
  group: 'group',
  bot: 'bot',
}

export function parseStartParam(startParam: string | undefined): ParsedStartParam | null {
  if (!startParam) return null

  const parts = startParam.split('_')

  // Минимум должен быть шаг
  if (parts.length === 0) return null

  const stepKey = parts[0]
  const step = stepMap[stepKey]

  if (!step) return null

  // Только шаг (например: "welcome")
  if (parts.length === 1) {
    return { step, type: null, entityId: '' }
  }

  // Проверяем формат success_paid_type_entityId
  // Например: success_paid_channel_-1001234567890
  if (step === 'success' && parts[1] === 'paid') {
    if (parts.length < 4) {
      return { step, type: null, entityId: '', isPaid: true }
    }
    const typeKey = parts[2]
    const type = typeMap[typeKey] || null
    // entityId может содержать дефис, поэтому объединяем оставшиеся части
    const entityId = parts.slice(3).join('_')
    return { step, type, entityId, isPaid: true }
  }

  // Шаг + тип (например: "connection_channel")
  const typeKey = parts[1]
  const type = typeMap[typeKey] || null

  if (parts.length === 2) {
    return { step, type, entityId: '' }
  }

  // Шаг + тип + entityId (например: "success_channel_-1001234567890")
  // Или шаг + тип + entityId + counterId (например: "success-direct_channel_-1001234567890_12345678")
  // Или шаг + тип + entityId + counterId + yandexLogin (например: "success-direct_channel_-1001234567890_12345678_user123")

  // Для success-direct парсим counterId и yandexLogin
  // Формат: success-direct_type_entityId_counterId_yandexLogin
  // counterId = 0 означает ошибку создания счётчика
  if (step === 'success-direct' && parts.length >= 4) {
    // parts[0] = step, parts[1] = type, parts[2] = entityId, parts[3] = counterId, parts[4] = yandexLogin (опционально)
    const entityId = parts[2]
    const counterIdPart = parts[3]
    const possibleCounterId = parseInt(counterIdPart, 10)

    // Валидация counterId: должен быть числом >= 0
    if (!isNaN(possibleCounterId) && possibleCounterId >= 0) {
      // counterId = 0 означает ошибку создания счётчика, возвращаем undefined
      const counterId = possibleCounterId > 0 ? possibleCounterId : undefined
      const yandexLogin = parts.length >= 5 ? parts[4] : undefined
      return { step, type, entityId, counterId, yandexLogin }
    }
  }

  // entityId может содержать дефис, поэтому объединяем оставшиеся части
  const entityId = parts.slice(2).join('_')

  return { step, type, entityId }
}
